import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSettings } from '../../../../../contexts/Settings/SettingsProvider';
import { useSelectUserContext } from '../../../../../selectors';
import { AuthType } from '../../../../../types';
import {
	getAuthenticationProvider,
	switchOrganization,
} from '../../../../../utils';
import { setTenantId } from '@innovyze/lib_get_service';

// LOCAL IMPORTS (OrgSwitcher deps)
import OrgList from './OrgList';
import OrgSwitcherMenuItem from './OrgSwitcherMenuItem';
import OrgText from './OrgText';
import useGetOrgList from '../../../../../hooks/useGetOrgList';
import {
	useTenantPickerListItems,
	useTenantPickerText,
} from './tenantPicker.utils';
import useAvoidBlankTenant from './useAvoidBlankTenant';

const TenantPicker = (): JSX.Element => {
	// STATES
	const [menuOpen, setMenuOpen] = useState(false);

	// STATES FROM HOOKS
	const { id } = useSelectUserContext();
	const listItems = useTenantPickerListItems();
	const text = useTenantPickerText();

	// TODO remove this as a requirement for setting up notifications flow
	useGetOrgList();

	// DERIVED STATES
	const isForgeFlow = getAuthenticationProvider() === AuthType.Forge;

	// METHODS
	const switchAction = (organization: string) => {
		localStorage.removeItem('chatMessages');
		if (isForgeFlow) {
			// Forge
			setTenantId(organization);
			window.location.href = '/';
		} else {
			// Auth0
			switchOrganization(id, organization).then(() => {
				window.location.href = '/';
			});
		}
	};

	/*
	 * orgListComponents is an array of JSX elements that are used to display the list of organizations
	 * the array is generated by mapping over the orgList array and return a component
	 */
	const orgListComponents = useMemo(
		() =>
			listItems.map(item => (
				<OrgSwitcherMenuItem
					closeMenu={() => setMenuOpen(false)}
					_id={item.id}
					name={item.name}
					switchAction={switchAction}
					key={item.id}
				/>
			)),
		[listItems, switchAction],
	);

	useAvoidBlankTenant(switchAction);

	// RENDER
	return (
		<>
			<OrgText
				orgText={text}
				openMenu={() => setMenuOpen(true)}
				orgListComponents={orgListComponents}
			/>
			<OrgList
				setMenuOpen={setMenuOpen}
				menuOpen={menuOpen}
				orgList={orgListComponents}
			/>
		</>
	);
};

export default TenantPicker;
